import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  CheckList,
  KeyStats,
  Callout,
  ResponsiveTable,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Solfilm · Företag",
  title: "Solfilm för kontor — guide till energibesparing, bländning och inomhusklimat (2026)",
  tldr:
    "Solfilm reducerar solvärmen i kontorslokaler med 50 % och sänker kylkostnaderna med 15–35 %. Lär dig vilka kontor som tjänar mest, hur återbetalningstiden räknas och vad 3M-auktorisation innebär. BBN Nordic — installatör sedan 2008.",
  readTimeMin: 10,
  heroImage: IMAGES.solfilmHero,
  path: "/guider/solfilm-kontor",
  updated: "2026-07-23",
  published: "2026-06-16",
  faqs: [
    {
      q: "Hur lång tid tar det att mäta och offerera en kontorslokal?",
      a: "Ett platsbesök och offert tar normalt 1–3 arbetsdagar från att vi fått era uppgifter. För större fastigheter ber vi om ritningar eller fasadmått i förväg.",
    },
    {
      q: "Kan vi filma under pågående verksamhet?",
      a: "Ja, invändig montering sker rum för rum utan att andra delar av kontoret påverkas. Många kunder väljer att vi jobbar i de rum som inte är belagda, sedan byter vi.",
    },
    {
      q: "Vad händer med solfilmen om vi byter hyresgäst?",
      a: "Filmen sitter kvar och garantin följer med fastigheten. Det är en tillgång i uthyrningssituationen — du kan kommunicera att lokalen har dokumenterad energioptimering med garanterat underhållsfritt skydd i 10 år.",
    },
    {
      q: "Går det att montera film på nyligen installerade energiglas?",
      a: "Ja, men glastypen avgör vilket filmval som är lämpligt. Vi kontrollerar alltid glasets specifikation (U-värde, befintliga beläggningar) innan vi väljer film, för att undvika risken för termiska spänningar.",
    },
    {
      q: "Hur märks effekten av filmen för medarbetarna?",
      a: "De flesta kontorskunder vi följt upp med rapporterar att förändringen är märkbar direkt. Specifikt: bländningsproblemet vid skärmar försvinner, de varmaste eftermiddagarna blir hanterliga utan att dra ner persienner, och klagomålen på inomhusklimatet minskar markant.",
    },
  ],
  related: [
    { to: "/solfilm-foretag", label: "Solfilm för företag — produktöversikt" },
    { to: "/3m-prestige", label: "3M Prestige-serien — datablad" },
    { to: "/guider/solfilm-vs-persienner", label: "Solfilm eller persienner? — jämförelse" },
    { to: "/guider/underhall-livslangd", label: "Skötsel & livslängd" },
  ],
};

export const Route = createFileRoute("/guider/solfilm-kontor")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "−50 %", label: "Solvärme (TSER)", sub: "3M Prestige 70" },
            { value: "15–35 %", label: "Lägre kylkostnad", sub: "typiskt sommarsnitt" },
            { value: "3–7 år", label: "Återbetalning", sub: "vid normal kyldrift" },
            { value: "99 %", label: "UV-skydd", sub: "skyddar inredning" },
          ]}
          source="3M datablad + BBN-kommersiella projekt"
        />

        <p>
          Kontorslokaler med glasfasad är en av de bästa tillämpningarna för solfilm.
          Kombinationen av stora glaspartier, kontinuerlig beläggning under dagtid och
          höga krav på jämn inomhustemperatur gör att effekten av rätt film är
          omedelbar, mätbar och varaktig.
        </p>
        <p>
          Men det finns stora skillnader i hur mycket ett specifikt kontor tjänar på
          film — skillnader som beror på fasadens orientering, glastyp, kylsystemets
          kapacitet och hur medarbetarna faktiskt upplever klimatet. Den här guiden ger
          dig verktyg att bedöma om film är rätt för din lokal och vad du kan förvänta
          dig.
        </p>

        <h2>Varför kontor overhettar och vad det kostar</h2>
        <p>
          Ett sydvänt kontorsfönster på 1,5 x 2 meter tar in 2 400 watt solinstrålning
          vid direkt sol på sommarmiddagen. Det är mer än en vanlig hushållsugn på max.
          Multiplicera det med 40 fönster i en kontorsfastighet och du förstår varför
          kylsystem i söderorienterade kontorsbyggnader ofta går på 100 % kapacitet
          under 8–12 veckor om sommaren.
        </p>
        <p>Det kostar på tre sätt:</p>
        <p>
          <strong>Energikostnad:</strong> Kylsystem i kommersiella fastigheter är ett av
          de tyngsta energiposterna under sommaren. En glasad kontorslokal på 500 kvm
          med hög solinstrålning kan kyla för 30 000–60 000 kr per år i elenergi.
          Solfilm reducerar den siffran med 15–35 % — det ger en återbetalningstid på
          3–7 år beroende på installationskostnad.
        </p>
        <p>
          <strong>Slitage på kylsystem:</strong> Att köra ett kylsystem på toppkapacitet
          under lång tid förkortar livslängden och ökar underhållskostnaderna. Film
          minskar de toppar som sliter mest.
        </p>
        <p>
          <strong>Produktivitets- och trivselförluster:</strong> Det är svårare att
          kvantifiera men lika verkligt. Medarbetare som jobbar i 27-gradig
          kontorslokal arbetar sämre och trivs sämre. Bländning på skärmar tvingar till
          felvinklar och persienner dragna mitt på dagen. Det är inte marginella
          effekter.
        </p>

        <GuideInlineCta />

        <h2>Vilka kontor tjänar mest?</h2>
        <p>
          <strong>Glasade kontorsfastigheter med söder- och västerläge</strong> är den
          tydligaste kategorin. Spegelglasade kontorshus med mycket fönsteryta mot söder
          är den situation som ger kortast återbetalningstid och mest märkbar
          komfortförbättring.
        </p>
        <p>
          <strong>Öppna kontorslandskap med sydvända skärmplatser</strong> är ett
          specifikt problem. Bländning på skärmar är inte bara irriterande — den driver
          medarbetare att dra ner persienner, vilket innebär att de sedan jobbar under
          lysrör hela dagen. Film eliminerar bländningen och låter naturligt ljus stanna
          kvar.
        </p>
        <p>
          <strong>Äldre kontorsfastigheter med 2-glas</strong> har den sämsta
          värmeisoleringen och den tydligaste effekten av film. Nyare byggnader med
          modern energiglastring har redan ett visst inbyggt skydd, men det räcker
          sällan för de värsta fasaderna.
        </p>
        <p>
          <strong>Serverrum och teknikrum med fönsterexponering</strong> är ett fall för
          sig. Här är kravet på stabil temperatur strikt, och ett enda fönster med
          solinstrålning kan skapa dramatiska temperaturvariationer. Film är ofta
          billigare och mer effektivt än att uppgradera kylkapaciteten.
        </p>

        <h2>3M Prestige-serien för kontor — tekniska data</h2>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Film</th>
              <th>VLT (ljus in)</th>
              <th>TSER (värme ut)</th>
              <th>Bländningsreduktion</th>
              <th>Passar bäst för</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Prestige 70</td>
              <td>68 %</td>
              <td>50 %</td>
              <td>Måttlig</td>
              <td>Mötesrum, representativa lokaler</td>
            </tr>
          </tbody>
        </ResponsiveTable>
        <p className="text-sm text-muted-foreground">
          Källa: 3M datablad. VLT = Visible Light Transmittance. TSER =
          Total Solar Energy Rejected.
        </p>

        <GuideInlineCta />

        <h2>Invändig eller utvändig montering för kontor?</h2>
        <p>
          De flesta kontorsinstallationer sker invändigt — det är enklare, billigare och
          filmen är skyddad från yttre påverkan. Men det finns situationer där utvändig
          montering är att föredra:
        </p>
        <p>
          <strong>Moderna isolerglasrutor med absorberande mellanskikt</strong> kan vid
          invändig montering ackumulera värme i glaset på ett sätt som skapar termiska
          spänningar. I de fallen väljer vi Prestige Exterior som är designad för
          utvändigt bruk med härdad ytbeläggning.
        </p>
        <p>
          <strong>Fasader där arkitekten kräver spegelfasadeffekt</strong> väljer ibland
          utvändig silverfilm. Vi monterar det men vi informerar alltid om skillnaden i
          estetik och underhållskrav.
        </p>
        <p>
          <strong>Höga kontorsfastigheter</strong> kräver skylift eller
          fönstervårdsgondol vid utvändig montering, vilket ökar installationskostnaden
          markant. Invändig montering kan då göras etagevis med vanlig ställning.
        </p>
        <Callout variant="info" title="Tumregel för isolerglas">
          <p>
            På modernt 3-glas med invändigt low-e-skikt rekommenderas Prestige 70
            invändigt. På äldre 2-glas eller kontorshus med absorberande tonat glas
            väljer vi ofta utvändig montering för att undvika risken med termiska
            spänningar i rutan.
          </p>
        </Callout>

        <h2>Hur en kontorsinstallation fungerar i praktiken</h2>
        <p>
          Det vi ser i de flesta fastighetsuppdrag är att processen ser ungefär likadan
          ut oavsett om det handlar om 200 eller 2 000 kvm:
        </p>
        <p>
          Fastighetsskötaren eller kontorschefen hör av sig för att hyresgästerna klagat
          på värme under flera somrar. Vi gör ett platsbesök, mäter fönsterytorna och
          identifierar de mest kritiska fasaderna. Det vanligaste är att söder- och
          västfasader ska filmas, medan norr- och östfasader lämnas.
        </p>
        <p>
          Vi lämnar offert per kvm, specificerat per filmtyp och fasad, med uppskattad
          återbetalningstid baserad på lokalens kylkostnad. Installationen planeras
          tillsammans med er så att den stör verksamheten så lite som möjligt. En
          kvalificerad installatörsgrupp monterar normalt 80–120 kvm per dag.
        </p>
        <p>
          Garantibevis utfärdas per objekt och inkluderar 10 års produktgaranti från 3M
          och 10 års arbetsgaranti från BBN Nordic.
        </p>

        <GuideInlineCta />

        <h2>Vad innebär 3M-auktorisation för dig som kund?</h2>
        <p>
          Det finns installatörer som monterar 3M-film utan att vara auktoriserade. Det
          är inte olagligt, men det innebär att 3M:s produktgaranti inte gäller —
          garantin är villkorad av att montering skett av auktoriserad installatör
          enligt 3M:s anvisningar.
        </p>
        <p>
          BBN Nordic är auktoriserad av 3M Svenska AB sedan 2008. Det innebär att vi är
          utbildade och certifierade av 3M, att vi kan lämna 10 års produktgaranti med
          3M som garant, och att vi har tillgång till 3M:s tekniska support vid ovanliga
          eller komplexa glastyper.
        </p>
        <p>
          För en fastighetsägare eller förvaltare innebär det att garantin faktiskt
          håller om filmen av något skäl inte presterar som avsett — inte som en
          diskussion med en lokal installatör, utan med ett globalt företag med
          garantiprogram.
        </p>
        <CheckList
          items={[
            "Kylenergi sommarmånaderna: −15 till −35 %",
            "Toppbelastning på kylaggregat: −15 till −25 % (förlänger livslängden)",
            "Bländningsklagomål från personal: minskar till nära noll",
            "Återbetalning: 3–7 år vid normal sommarkylning",
          ]}
        />
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
